/*************************************************************************
* Copyright (c) 2025 Omnineura AI Technologies Pvt Limited.
* All rights reserved.
* This code is licensed under the 'Omnineura AI License' found in the LICENSE file.
* Unauthorized copying, sharing, or use of this code is prohibited.
* The intellectual and technical concepts are proprietary to Omnineura AI Technologies Pvt Limited.
* Author: surya@Omnineura.ai
* Date: 11-09-2025
*************************************************************************/

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { createPaymentSessionApi, verifyPaymentApi } from "../services/api";

// Create Razorpay session
export const createPaymentSession = createAsyncThunk(
  "payment/createPaymentSession",
  async ({ subscription, plantype, coupon }, { rejectWithValue }) => {
    try {
      const res = await createPaymentSessionApi(subscription, plantype, coupon);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Verify payment
export const verifyPayment = createAsyncThunk(
  "payment/verifyPayment",
  async ({ razorpayResponse, paymentData }, { rejectWithValue }) => {
    try {
      const res = await verifyPaymentApi(razorpayResponse, paymentData);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const paymentReducer = createSlice({
  name: "payment",
  initialState: {
    session: null,
    verification: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearPaymentState: (state) => {
      state.session = null;
      state.verification = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Session
      .addCase(createPaymentSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPaymentSession.fulfilled, (state, action) => {
        state.loading = false;
        state.session = action.payload;
      })
      .addCase(createPaymentSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Verify Payment
      .addCase(verifyPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.verification = action.payload;
      })
      .addCase(verifyPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearPaymentState } = paymentReducer.actions;
export default paymentReducer.reducer;
